import { getParty, type Phase } from '@val/shared'
import type { ReactNode } from 'react'
import { useCountingProgress } from '../hooks/useCountingProgress.ts'
import { clockTime, int, pct } from '../lib/format.ts'
import { ProgressBar } from './Bits.tsx'
import './countingprogressnotice.css'

interface Props {
  phase: Phase | undefined
  /** Förklarande text ovanför räkningsstatusen, t.ex. varför kommunens resultat saknas. */
  message: ReactNode
}

/**
 * Visas i stället för en "död" felruta medan Ales egen mandatfördelning
 * ännu inte är publicerad: hur långt räkningen kommit, baserat på
 * riksdagsvalet – samma valdistrikt räknar riksdags-, region- och
 * kommunvalet, så den brukar ligga steget före.
 */
export function CountingProgressNotice({ phase, message }: Props) {
  const { progress, loading } = useCountingProgress(phase, true)

  return (
    <div className="notice progress-notice">
      <p className="progress-notice__message">{message}</p>

      {progress ? (
        <div className="progress-notice__body">
          <div className="progress-notice__row">
            <span>Ale</span>
            <span className="tnum">
              {progress.totalAreas > 0
                ? `${int(progress.countedAreas)} av ${int(progress.totalAreas)} valdistrikt · ${pct(progress.countedRatio * 100)}`
                : 'väntar på första valdistriktet'}
            </span>
          </div>
          <ProgressBar ratio={progress.countedRatio} />

          <div className="progress-notice__row progress-notice__row--dim">
            <span>Hela landet</span>
            <span className="tnum">
              {int(progress.nationalCountedAreas)} av {int(progress.nationalTotalAreas)} valdistrikt
              · {pct(progress.nationalCountedRatio * 100)}
            </span>
          </div>

          <p className="progress-notice__note">
            Baserat på riksdagsräkningen, som brukar ligga steget före kommunens egen
            sammanställning – ett räknat valdistrikt betyder alltså inte att Ales kommunresultat
            finns än. Uppdaterad {clockTime(progress.updatedAt)}.
          </p>

          {progress.riksdagVotes && progress.riksdagVotes.length > 0 && (
            <div className="progress-notice__preview">
              <p className="progress-notice__note">
                Riksdagsvalet i Ale hittills (inte kommunvalet):
              </p>
              <ul className="progress-notice__parties">
                {progress.riksdagVotes.map((p) => (
                  <li key={p.code}>
                    <span
                      className="progress-notice__dot"
                      style={{ background: getParty(p.code).color }}
                    />
                    <span className="progress-notice__partyname">{getParty(p.code).shortName}</span>
                    <span className="tnum">{pct(p.pct)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        loading && <p className="progress-notice__note">Hämtar räkningsstatus …</p>
      )}
    </div>
  )
}
